<template>
  <div class="component">
    <!-- Dark header section -->
    <div class="page-heading stk-p-8">
      <PageHeading
        titleClassname="tw-text-inherit"
        subtitleClassname="tw-text-inherit tw-opacity-50"
      >
        <template v-slot:title>
          Meeting <span class="assistant">Assistant</span>
        </template>
        <template v-slot:subtitle>
          Automatically capture key financial info, and coordinate tasks
        </template>
      </PageHeading>

      <div class="features">
        <div class="feature" v-for="feature in features">
          <Icon class="feature-icon" :icon="feature.icon" />
          <div>
            <strong>{{ feature.title }}</strong>
            <span>{{ feature.description }}</span>
          </div>
        </div>
      </div>
    </div>

    <Stack class="stk-p-8">
      <!-- Header Section -->
      <div class="tw-flex tw-justify-between tw-items-center">
        <div class="tw-flex tw-gap-2">
          <Title>{{ totalMeetings }} Meetings</Title>
          <Badge status="warning"
            ><strong>{{ meetingsThisMonth }}</strong> this month</Badge
          >
          <Badge status="success"
            ><strong>{{ Math.round(totalMeetings * 0.5) }}</strong> hours
            saved</Badge
          >
        </div>

        <div class="tw-flex tw-items-center tw-gap-5">
          <button
            v-if="needInstructions && !showInstructions"
            class="stk-reset show-instructions"
            @click="showInstructions = true"
            size="3_5"
          >
            Complete your setup
          </button>
          <MeetingBotSettingsButton />
          <AddMeetingButton size="is-large" @meeting-created="getMeetings" />
        </div>
      </div>

      <LoaderWithText v-if="isLoading"> Fetching Meetings… </LoaderWithText>

      <template v-else class="meetings-content">
        <MeetingsOnboarding
          v-if="needInstructions && showInstructions"
          :showInstructions="showInstructions"
          :hasCrmConnected="hasCrmConnected"
          :hasCalendarConnected="hasCalendarConnected"
          :needInstructions="needInstructions"
          @onHideInstructions="showInstructions = false"
        />

        <Pills class="pills-container">
          <PillsList>
            <PillItem
              :active="selectedTab === 'upcoming'"
              @click="selectedTab = 'upcoming'"
              icon="fas fa-calendar"
            >
              <span>Upcoming Meetings</span>
              <Badge :status="badgeStatus('upcoming', upcomingMeetings)">
                {{ upcomingMeetings.length }}
              </Badge>
            </PillItem>
            <PillItem
              :active="selectedTab === 'past'"
              @click="selectedTab = 'past'"
              icon="fas fa-calendar-check"
            >
              <span>Past Meetings</span>
              <Badge :status="badgeStatus('past', pastMeetings)">
                {{ pastMeetings.length }}
              </Badge>
            </PillItem>
          </PillsList>
        </Pills>

        <MeetingsTable
          v-if="selectedTab === 'upcoming'"
          ref="meetingsTable"
          :meetings="upcomingMeetings"
          variant="upcoming"
          @view-meeting="handleViewMeeting"
          @updated="getMeetings"
        />

        <MeetingsTable
          v-else-if="selectedTab === 'past'"
          ref="meetingsTable"
          :meetings="pastMeetings"
          variant="past"
          @view-meeting="handleViewMeeting"
          @updated="getMeetings"
        />
      </template>
    </Stack>
  </div>
</template>

<script>
import MeetingBotSettingsButton from "@frontend/ui/features/meetings/components/MeetingBotSettingsButton.vue";
import {
  Badge,
  Button,
  Icon,
  LoaderWithText,
  PageHeading,
  PillItem,
  Pills,
  PillsList,
  Stack,
  Subtitle,
  Title,
} from "@stakeui";
import AddMeetingButton from "../components/AddMeetingButton.vue";
import MeetingsOnboarding from "../components/MeetingsOnboarding.vue";
import MeetingsTable from "../components/MeetingsTable.vue";

import { useAdvisorStore } from "@frontend/ui/stores/advisorStore.js";
import { mapStores } from "pinia";

const STORAGE_KEY = "meetingsInstructions";

export default {
  name: "MeetingLaunchpad",
  components: {
    MeetingsTable,
    AddMeetingButton,
    MeetingBotSettingsButton,
    MeetingsOnboarding,
    Badge,
    Button,
    Icon,
    LoaderWithText,
    Pills,
    PillsList,
    PillItem,
    PageHeading,
    Stack,
    Title,
    Subtitle,
  },
  data() {
    return {
      totalMeetings: 0,
      meetingsThisMonth: 0,
      pastMeetings: [],
      upcomingMeetings: [],
      isLoading: false,
      selectedTab: "upcoming",
      features: [
        {
          icon: "fas fa-microphone",
          title: "Recording & Transcription",
          description: "Seamless meeting capture",
        },
        {
          icon: "fas fa-brain",
          title: "AI Summaries",
          description: "Auto-extract key points",
        },
        {
          icon: "fas fa-tasks",
          title: "Task Management",
          description: "Track meeting follow-ups",
        },
        {
          icon: "fas fa-calendar",
          title: "Calendar Integration",
          description: "Auto-schedule meetings",
        },
      ],
      showInstructions: window.localStorage.getItem(STORAGE_KEY) !== "false",
    };
  },
  computed: {
    ...mapStores(useAdvisorStore),
    hasCrmConnected() {
      const connections = Object.keys(
        this.advisorStore.activeOAuthConnections || {}
      );
      return connections.some((item) =>
        ["Wealthbox", "Redtail", "Salesforce"].includes(item)
      );
    },
    hasCalendarConnected() {
      const connections = Object.keys(
        this.advisorStore.activeOAuthConnections || {}
      );
      return connections.some((item) =>
        ["Google", "Google Calendar", "Outlook"].includes(item)
      );
    },
    needInstructions() {
      return !this.hasCrmConnected || !this.hasCalendarConnected;
    },
    headers() {
      return {
        "X-CSRFToken": this.$root.$data.csrfToken,
        "Content-Type": "application/json",
      };
    },
  },
  created() {
    this.getMeetings();
  },
  methods: {
    async getMeetings() {
      console.log("GET MEETINGS CALLED");
      this.isLoading = true;
      try {
        const res = await fetch("/api/v1/meetings/", {
          method: "GET",
          headers: this.headers,
        });
        const data = await res.json();
        const currentDate = new Date();

        this.pastMeetings = data.meetings.filter(
          (meeting) => new Date(meeting.meeting_date).getTime() < currentDate
        );
        this.upcomingMeetings = data.meetings.filter(
          (meeting) => new Date(meeting.meeting_date).getTime() >= currentDate
        );
        this.totalMeetings = data.meetings.length;

        // Calculate meetings this month
        const thisMonth = new Date().getMonth();
        this.meetingsThisMonth = data.meetings.filter((meeting) => {
          const meetingDate = new Date(meeting.meeting_date);
          return meetingDate.getMonth() === thisMonth;
        }).length;
      } catch (error) {
        console.error("Failed to fetch meetings:", error);
      } finally {
        this.isLoading = false;
      }
    },
    handleViewMeeting(meeting) {
      console.log("Viewing meeting:", meeting);
    },
    badgeStatus(tabName, meetings) {
      if (this.selectedTab !== tabName) {
        return "disabled";
      }

      if (meetings.length > 0) {
        return "success";
      }

      return "info";
    },
  },
  watch: {
    showInstructions(newValue) {
      window.localStorage.setItem(STORAGE_KEY, newValue);
    },
  },
};
</script>

<style scoped>
.component {
  --accent: #6c63ff;
}

.page-heading {
  background-color: #1a1a2e;
  color: white;
}

.assistant {
  color: var(--accent);
}

.features {
  align-items: center;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1em;
  margin-top: 1em;
}

.feature {
  align-items: center;
  display: flex;
  gap: 1em;
  line-height: 1.25;

  strong {
    display: block;
    color: inherit;
    font-weight: 500;
  }

  span {
    opacity: 0.5;
    font-size: 0.875em;
  }
}

.feature-icon {
  color: var(--accent);
}

.show-instructions {
  color: #577eff;
  background-color: #577eff26;
  border-radius: 0.5em;
  padding: 0.25em 0.75em;
  font-weight: 500;
  cursor: pointer;
}

/* Content container */
.content-container {
  border-radius: 8px;
  background-color: white;
}

/* Top Bar */
.count-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

/* Feature Cards */
.feature-panel,
.stats-panel {
  background-color: white;
  padding: 1.5rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #f0f0f0;
}

.feature-card {
  display: flex;
  align-items: flex-start;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.feature-card:hover {
  transform: translateY(-2px);
}

.feature-title {
  font-weight: 600;
  font-size: 0.875rem;
  color: #374151;
  display: block;
  margin-bottom: 0.25rem;
}

.feature-description {
  font-size: 0.8rem;
  color: #6b7280;
  margin: 0;
}

/* Stats Cards */
.stat-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  transition: transform 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  background-color: #eef3ff;
  border-radius: 0.5rem;
  margin-bottom: 0.75rem;
  color: #6c63ff;
  font-size: 1.25rem;
}

.stat-label {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 0.25rem 0;
  text-align: center;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #374151;
  margin: 0;
  text-align: center;
}

/* Meetings section */
.meetings-content {
  background-color: white;
  border-radius: 0.5rem;
}

:deep(.table) {
  width: 100%;
  border-collapse: collapse;
}

:deep(.table th) {
  background-color: #f9f9f9;
  padding: 1rem 0.75rem;
  border-bottom: 1px solid #f0f0f0;
  font-weight: 600;
  color: #374151;
}

:deep(.table td) {
  padding: 1rem 0.75rem;
  border-bottom: 1px solid #f0f0f0;
}

:deep(.table tr:hover) {
  background-color: #f5f7fb;
}
</style>
